import { useState, useCallback, useEffect } from 'react';
import { MdClose, MdTune, MdCheck } from 'react-icons/md';

const DEFAULT_FILTERS = {
  priceMin: '',
  priceMax: '',
  ratingMin: '',
  verifiedOnly: false,
};

/**
 * Filters panel: desktop = collapsible side panel / inline; mobile = full-screen drawer.
 * Emits filter values; parent applies them to results (client-side or API).
 */
export default function FiltersDrawer({
  open,
  onClose,
  filters = DEFAULT_FILTERS,
  onApply,
  onReset,
  isMobile = false,
}) {
  const [local, setLocal] = useState(filters);
  useEffect(() => {
    if (open) setLocal(filters);
  }, [open, filters]);

  const handleApply = useCallback(() => {
    onApply?.(local);
    onClose?.();
  }, [local, onApply, onClose]);

  const handleReset = useCallback(() => {
    setLocal(DEFAULT_FILTERS);
    onReset?.();
  }, [onReset]);

  const content = (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 pb-3">
        <h3 className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <MdTune className="text-lg" /> Filteri
        </h3>
        {isMobile && (
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
            aria-label="Zatvori"
          >
            <MdClose className="text-xl" />
          </button>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Cena (RSD/h)
        </label>
        <div className="flex gap-2">
          <input
            type="number"
            min={0}
            placeholder="Min"
            value={local.priceMin}
            onChange={(e) => setLocal((f) => ({ ...f, priceMin: e.target.value }))}
            className="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
          />
          <input
            type="number"
            min={0}
            placeholder="Max"
            value={local.priceMax}
            onChange={(e) => setLocal((f) => ({ ...f, priceMax: e.target.value }))}
            className="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Min. ocena
        </label>
        <select
          value={local.ratingMin}
          onChange={(e) => setLocal((f) => ({ ...f, ratingMin: e.target.value }))}
          className="w-full rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
        >
          <option value="">Bilo koja</option>
          <option value="3">3+</option>
          <option value="4">4+</option>
          <option value="4.5">4.5+</option>
          <option value="4.8">4.8+</option>
        </select>
      </div>

      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={local.verifiedOnly}
          onChange={(e) => setLocal((f) => ({ ...f, verifiedOnly: e.target.checked }))}
          className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        <span className="text-sm text-gray-700 dark:text-gray-300">Samo provereni profili</span>
      </label>

      <div className="flex gap-2 pt-2">
        <button
          type="button"
          onClick={handleReset}
          className="flex-1 py-2 rounded-xl border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          Poništi
        </button>
        <button
          type="button"
          onClick={handleApply}
          className="flex-1 py-2 rounded-xl bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 flex items-center justify-center gap-1"
        >
          <MdCheck className="text-lg" /> Primeni
        </button>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <>
        {open && (
          <div
            className="fixed inset-0 z-[9999] lg:hidden"
            role="dialog"
            aria-modal="true"
            aria-label="Filteri"
          >
            <div
              className="absolute inset-0 bg-black/50"
              onClick={onClose}
              aria-hidden
            />
            <div className="absolute bottom-0 left-0 right-0 max-h-[85vh] overflow-y-auto rounded-t-2xl bg-white dark:bg-gray-800 shadow-xl border-t border-gray-200 dark:border-gray-700">
              {content}
            </div>
          </div>
        )}
      </>
    );
  }

  // Desktop: inline collapsible panel
  if (!open) return null;
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden">
      {content}
    </div>
  );
}

export { DEFAULT_FILTERS };
